import { gql } from '@graphql/__generated__';

// Mass-mail campaigns list. Counters are nested aggregates on the email_messages
// relation (status values mirror the legacy loader: sent / pending / error, total
// = count(*)). Server-side pagination: power users have thousands of campaigns,
// and each row costs 4 aggregate subqueries.
export const EMAIL_CAMPAIGNS_LIST = gql(/* GraphQL */ `
  query EmailCampaignsList(
    $where: email_campaigns_bool_exp!
    $limit: Int!
    $offset: Int!
    $order_by: [email_campaigns_order_by!]
  ) {
    email_campaigns_aggregate(where: $where) {
      aggregate {
        count
      }
    }
    email_campaigns(
      where: $where
      order_by: $order_by
      limit: $limit
      offset: $offset
    ) {
      id
      status
      created_at
      user {
        id
        first_name
        last_name
      }
      first_message: email_messages(limit: 1, order_by: { created_at: asc }) {
        id
        subject_template
        body_template
        activity {
          buyer_lead {
            lot {
              id
              title
              lot_reference
              property {
                id
                property_images(order_by: { order_nr: asc }, limit: 1) {
                  id
                  image {
                    id
                    url
                  }
                }
              }
            }
          }
        }
      }
      total: email_messages_aggregate {
        aggregate {
          count
        }
      }
      sent: email_messages_aggregate(where: { status: { _eq: "sent" } }) {
        aggregate {
          count
        }
      }
      error: email_messages_aggregate(where: { status: { _eq: "error" } }) {
        aggregate {
          count
        }
      }
      pending: email_messages_aggregate(where: { status: { _eq: "pending" } }) {
        aggregate {
          count
        }
      }
    }
  }
`);

// Single campaign for the route-driven preview drawer (/emails/campaigns/:id).
// Same field shape as the list rows; fetched by pk so deep links work even when
// the campaign is not on the currently loaded page. RLS applies to by_pk too.
export const EMAIL_CAMPAIGN_PREVIEW = gql(/* GraphQL */ `
  query EmailCampaignPreview($id: uuid!) {
    email_campaigns_by_pk(id: $id) {
      id
      status
      created_at
      user {
        id
        first_name
        last_name
      }
      first_message: email_messages(limit: 1, order_by: { created_at: asc }) {
        id
        subject_template
        body_template
        activity {
          buyer_lead {
            lot {
              id
              title
              lot_reference
              property {
                id
                property_images(order_by: { order_nr: asc }, limit: 1) {
                  id
                  image {
                    id
                    url
                  }
                }
              }
            }
          }
        }
      }
      total: email_messages_aggregate {
        aggregate {
          count
        }
      }
      sent: email_messages_aggregate(where: { status: { _eq: "sent" } }) {
        aggregate {
          count
        }
      }
      error: email_messages_aggregate(where: { status: { _eq: "error" } }) {
        aggregate {
          count
        }
      }
      pending: email_messages_aggregate(where: { status: { _eq: "pending" } }) {
        aggregate {
          count
        }
      }
    }
  }
`);

// Per-recipient delivery detail for the preview (drilldown), fetched lazily when
// the "Show details" dialog opens. All three roles (agent / tenant_admin / user)
// can read `to` / `error_message` on their OWN campaigns' messages. Errors sort
// first (enum order: error < pending < sent), so if the cap is ever hit only the
// sent tail is truncated (the dialog then shows a "showing first N" note).
// Limit 2500 covers 100% of campaigns ever sent (prod max = 2317 recipients;
// only 15 of 13.9k campaigns exceed 1000), so truncation never triggers in
// practice; the note is just the safety net for a hypothetical future outlier.
export const EMAIL_CAMPAIGN_RECIPIENTS = gql(/* GraphQL */ `
  query EmailCampaignRecipients($id: uuid!) {
    email_campaigns_by_pk(id: $id) {
      id
      recipients: email_messages(
        order_by: [{ status: asc }, { created_at: asc }]
        limit: 2500
      ) {
        id
        to
        status
        error_message
      }
    }
  }
`);

// Listings picker for the "new mass mail" CTA — the agent's visible listings
// that have at least one buyer lead. RLS scopes visibility.
// `order_by` is a nullable variable on purpose: the `buyer_leads_aggregate`
// count predicate in `$where` is evaluated per candidate lot, so ANY ordering
// forces it over every lot of the tenant before the limit applies. The caller
// ranks by buyer count on the unfiltered dialog and passes null once a search
// term is typed, which lets the LIMIT short-circuit the scan.
export const MASS_MAIL_LISTINGS = gql(/* GraphQL */ `
  query MassMailListings(
    $where: lots_bool_exp!
    $limit: Int!
    $order_by: [lots_order_by!]
  ) {
    lots(where: $where, order_by: $order_by, limit: $limit) {
      id
      title
      lot_reference
      property {
        id
        route
        street_number
        postcode
        locality
        property_images(order_by: { order_nr: asc }, limit: 1) {
          id
          image {
            id
            url
          }
        }
      }
      buyer_leads_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`);
